/**
 * 블소 문파
 */
import { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import Header from '@features/content/Header';
import { apiSearchSlice } from '@features/api/apiSearchSlice';
import { setRequestToSearchParams } from '@utils/index';
import { ALIAS, CUR_NETWORK, FILTER_DATA, NETWORK, SEARCH_PARAMS, SERVER_TYPE, SERVICE_ALIAS } from '@constants/index';
import { getLocalData } from '@features/api/apiLocalSlice';
import NoData from '../../components/contents/common/NoData';
import { useOption } from '@hooks/useOption';
import { useAppSelector } from '@hooks/defineRtkTypedHook';
import usePrevious from '@hooks/usePrevious';
import { selectL10N, setAlias } from '@features/ui/UiSlice';
import Filter, { setl10nKeyToTitle } from '@components/contents/common/Filter';
import GuildArticle from '@components/contents/article/GuildArticle';
import Paging from '@components/contents/common/Paging';
import type { IContentData, IGuild, ILoaderData, l10nFilterType, l10nType } from '@type/index';

let dropdownMap: { [key: string]: any } = {};

export const GuildLoader =
  (dispatch, option: IOption) =>
  async ({ request }): Promise<IGuild | null> => {
    const searchParams = setRequestToSearchParams(request);

    // alias 변경 - searchParams 변경에 사용됨
    dispatch(setAlias(ALIAS.GUILD));

    if (!searchParams?.keyword) {
      return null;
    }

    for (let key in searchParams) {
      !searchParams[key] && delete searchParams[key]; // serverId 등 string으로 보내면 안됨
    }

    return CUR_NETWORK === NETWORK.OPDEV
      ? await getLocalData(ALIAS.GUILD, searchParams)
      : await dispatch(apiSearchSlice?.endpoints?.getGuild?.initiate({ option, params: searchParams }));
  };

// serverId 필터 데이터 가공
const setFilterServerData = (option: IOption, defaultData?: l10nFilterType) => {
  const { serverData } = option || {};
  const newServerData = {} as {[serverType: string]: l10nFilterType};

  if (Array.isArray(serverData) && serverData?.length > 0) {
    serverData?.forEach((v) => {
      const { serverId, serverName, serverType } = v || {};
      const lowerServerType = serverType?.toLowerCase();

      if(lowerServerType) {
        if(!newServerData?.[lowerServerType]) {
          newServerData[lowerServerType] = defaultData ? [...defaultData] : [];
        }

        newServerData?.[lowerServerType]?.push({
          title: serverName,
          value: serverId,
        });
      }
    });
  }

  return newServerData;
};

interface IFilterComponentProps {
  count: number;
  l10n: l10nType | null;
}
const FilterComponent = ({ count, l10n }: IFilterComponentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const serverType = useMemo(() => searchParams?.get(SEARCH_PARAMS.SERVER_TYPE) ?? SERVER_TYPE[SERVICE_ALIAS.BNS].NEO, [searchParams]);
  const [activeServerType, setActiveServerType] = useState<string>(serverType!);
  const prevActiveServerType = usePrevious(activeServerType);
  const option = useOption();
  const originFilterData = useMemo(() => FILTER_DATA[option?.serviceAlias][ALIAS.GUILD], []) as {[key: string]: l10nFilterType};
  const filterData = useMemo(() => Object.entries(originFilterData), []);
  const filterServerIdData = useMemo(() => setFilterServerData(option, originFilterData?.[SEARCH_PARAMS.SERVER_ID]), [filterData]);
  
  // serverType 필터 변경 시, serverId 필터 초기화, 활성화 및 searchParams 제어
  useEffect(() => {
    // 초기 로드 시 api 중복 호출 되지 않도록 초기 로드인지 확인
    /*
      TODO. 이렇게 번거롭게 필터의 state값 생성해서, 해당 값이 변경될 때마다 searchParams를 바꿔주는 것은
      dropdown이 react 컴포넌트가 아닌 별도 js 모듈이어서 state 관련 흐름(searchParams 관련)이 깨짐...
      (ex) dropdown change callback에 searchParams 변경 코드 넣으면 searchParams 변경된 값이 업데이트 되지 않음...
      추후에 dropdown 자체를 react component 로 변경 필요함!!
    */
    if(prevActiveServerType && activeServerType && prevActiveServerType !== activeServerType) {
      const serverIdData = filterServerIdData[activeServerType];
      const initActiveValue = `${serverIdData[0]?.value ?? ''}`;

      dropdownMap[SEARCH_PARAMS.SERVER_ID]?.reset({ data: setl10nKeyToTitle(serverIdData, l10n!) });
      dropdownMap[SEARCH_PARAMS.SERVER_ID]?.activeValue([initActiveValue ?? '']);

      searchParams?.set(SEARCH_PARAMS.SERVER_TYPE, activeServerType);
      searchParams?.set(SEARCH_PARAMS.SERVER_ID, initActiveValue);
      searchParams?.set(SEARCH_PARAMS.PAGE, '1');
      setSearchParams(searchParams);
    }
  }, [activeServerType]);
  
  // serverType 변경 시 serverId 필터 초기화, 활성화 할 수 있도록 참조값 저장
  const setDropdownMap = useCallback(
    (data: { [key: string]: any }) => {
      dropdownMap = { ...dropdownMap, ...data };
    },
    [dropdownMap]
  );

  const changeServerTypeFilter = useCallback((name: string, activeData: { d1: string; d2?: string } | null) => {
    setActiveServerType(activeData?.d1 ?? SERVER_TYPE[SERVICE_ALIAS.BNS].NEO as string);
  }, []);

  return (
    <Header totalCount={count}>
      {Array.isArray(filterData) && filterData?.length > 0 ? 
        filterData?.map((data) => {
          const key = data[0];
          const value = data[1];
          const filterData = setl10nKeyToTitle(key === SEARCH_PARAMS.SERVER_ID ? filterServerIdData?.[serverType!] : value, l10n!);
          const hasAllBlankValue = value?.filter((v) => !v?.value)?.length > 0;
          const newOption = {
            ...option,
            name: data[0],
            useAllBlankValue: hasAllBlankValue,
          };

          const commonProps = { key, filterData, searchParams, setSearchParams, setDropdownMap, option: newOption };

          return key === SEARCH_PARAMS.SERVER_TYPE ? (
            <Filter {...{
              ...commonProps, 
              useEachChangeCallback: true,
              changeCallback: changeServerTypeFilter
             }} />
          ) : (
            <Filter {...commonProps} />
          );
        })
       : (
        <Fragment />
      )}
    </Header>
  );
};

interface IContentComponentProps {
  data: IContentData<IGuild>;
  l10n: l10nType | null;
}
const ContentComponent = ({ data, l10n }: IContentComponentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const serverType = useMemo(() => searchParams?.get(SEARCH_PARAMS.SERVER_TYPE) ?? SERVER_TYPE[SERVICE_ALIAS.BNS].NEO, [searchParams]);

  return (
    <Fragment>
      <div className="search-content">
        <ul className="column column-2">
          {data?.list.map((item, i) => (
            <li key={i} className="search-item">
              <GuildArticle {...{ item, l10n: l10n!, serverType }} />
            </li>
          ))}
        </ul>
      </div>
      <Paging endPage={data?.pagination?.endPage} showPage={5} l10n={l10n!} />
    </Fragment>
  );
};

const Guild = () => {
  const { data, isLoading, isError } = (useLoaderData() as ILoaderData<IContentData<IGuild>>) || {};
  const l10n = useAppSelector(selectL10N);

  return (
    <div className="search-result guild">
      <FilterComponent count={data?.pagination?.total} {...{l10n}} />
      {!isError && Array.isArray(data?.list) && data?.list?.length > 0 ? <ContentComponent {...{data, l10n}} /> : <NoData />}
    </div>
  );
};

export default Guild;
